import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Plus, ArrowLeft, Download, Calendar, Trash2 } from 'lucide-react';
import RatingControl from '../components/RatingControl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyAppraisals = () => {
  const { user } = useAuth();
  const [appraisals, setAppraisals] = useState([]);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAppraisalData, setNewAppraisalData] = useState({
    cycle_name: '',
    cycle_year: '',
    cycle_type: 'Annual'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appraisalsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/appraisals`),
        axios.get(`${API_URL}/settings`)
      ]);
      
      // HR Admins can see all appraisals, regular users see only their own
      const myAppraisals = user.role === 'HR_ADMIN' 
        ? appraisalsRes.data 
        : appraisalsRes.data.filter(a => a.employee_id === user.id);
      setAppraisals(myAppraisals);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load appraisals');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/appraisals/${selectedAppraisal.id}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appraisal_${selectedAppraisal.employee_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_name.replace(/\s/g, '_')}_${selectedAppraisal.cycle_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCreateAppraisal = async () => {
    try {
      await axios.post(`${API_URL}/appraisals`, newAppraisalData);
      toast.success('Appraisal created successfully');
      setShowCreateDialog(false);
      setNewAppraisalData({ cycle_name: '', cycle_year: '', cycle_type: 'Annual' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appraisal');
    }
  };

  const handleSaveDraft = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, selectedAppraisal);
      toast.success('Draft saved successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleSubmitSelf = async () => {
    try {
      await axios.put(`${API_URL}/appraisals/${selectedAppraisal.id}`, {
        ...selectedAppraisal,
        status: 'SUBMITTED_SELF'
      });
      toast.success('Self-assessment submitted successfully');
      setSelectedAppraisal(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit self-assessment');
    }
  };

  const handleDeleteAppraisal = async (appraisalId, cycleName) => {
    if (!window.confirm(`Are you sure you want to delete the appraisal "${cycleName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/appraisals/${appraisalId}`);
      toast.success('Appraisal deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete appraisal');
    }
  };

  const getStatusBadge = (status) => {
    const labels = {
      'DRAFT_SELF': 'Draft',
      'SUBMITTED_SELF': 'Pending Manager',
      'IN_MANAGER_REVIEW': 'Manager Review',
      'SECOND_LEVEL_REVIEW': 'Calibration',
      'FINALIZED': 'Finalized'
    };

    return (
      <Badge variant="outline" className="uppercase tracking-wider text-xs border border-slate-200" data-testid={`status-${status}`}>
        {labels[status]}
      </Badge>
    );
  };

  const getCycleTypeBadge = (cycleType) => {
    const colors = {
      'Annual': 'bg-blue-100 text-blue-800 border-blue-200',
      'Half-Yearly': 'bg-teal-100 text-teal-800 border-teal-200',
      'Q1': 'bg-green-100 text-green-800 border-green-200',
      'Q2': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Q3': 'bg-orange-100 text-orange-800 border-orange-200',
      'Q4': 'bg-purple-100 text-purple-800 border-purple-200'
    };

    return (
      <Badge className={`text-xs font-semibold border ${colors[cycleType] || colors['Annual']}`}>
        {cycleType || 'Annual'}
      </Badge>
    );
  };

  const getRatingDisplay = (rating) => {
    if (!rating) return <span className="text-slate-400">-</span>;
    return <span className="font-semibold text-slate-900" data-testid={`rating-${rating}`}>{rating}</span>;
  };

  if (loading) return <div className="text-sm text-slate-700">Loading...</div>;

  if (selectedAppraisal) {
    const isEditable = selectedAppraisal.status === 'DRAFT_SELF';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="appraisal-detail-title">Self Assessment</h1>
            <p className="text-sm text-slate-700 mt-2">{selectedAppraisal.cycle_name} {selectedAppraisal.cycle_year}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="border-slate-200 rounded-sm"
              data-testid="download-pdf-button"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedAppraisal(null)}
              className="border-slate-200 rounded-sm"
              data-testid="back-to-list-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to List
            </Button>
          </div>
        </div>

        <Card className="border border-slate-200 bg-white rounded-sm p-6 space-y-6">
          {/* Competencies */}
          <div>
            <h3 className="text-xl font-medium text-slate-900 mb-4">Competencies</h3>
            <div className="space-y-6">
              {selectedAppraisal.competencies.map((comp, idx) => (
                <div key={idx} className="border border-slate-200 bg-slate-50 p-4 rounded-sm" data-testid={`competency-${idx}`}>
                  <div className="font-semibold text-slate-900 mb-3">{comp.name}</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your Rating</Label>
                      {isEditable ? (
                        <RatingControl
                          value={comp.self_rating}
                          onChange={(rating) => {
                            const updated = { ...selectedAppraisal };
                            updated.competencies[idx].self_rating = rating;
                            setSelectedAppraisal(updated);
                          }}
                          testId={`self-rating-${idx}`}
                        />
                      ) : (
                        <div className="text-slate-900 font-semibold">{comp.self_rating || '-'}</div>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your Comments</Label>
                      {isEditable ? (
                        <Textarea
                          value={comp.self_comment || ''}
                          onChange={(e) => {
                            const updated = { ...selectedAppraisal };
                            updated.competencies[idx].self_comment = e.target.value;
                            setSelectedAppraisal(updated);
                          }}
                          className="border-slate-200 rounded-sm min-h-[80px]"
                          data-testid={`self-comment-${idx}`}
                        />
                      ) : (
                        <div className="text-sm text-slate-700">{comp.self_comment || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Manager Feedback (Read-only) */}
                  {(comp.manager_rating || comp.manager_comment) && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Manager Feedback</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Rating</div>
                          <div className="text-slate-900 font-semibold">{comp.manager_rating || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Comments</div>
                          <div className="text-sm text-slate-700">{comp.manager_comment || '-'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2nd Level Feedback (Read-only) */}
                  {(comp.second_level_rating || comp.second_level_comment) && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">2nd-Level Calibration</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Rating</div>
                          <div className="text-slate-900 font-semibold">{comp.second_level_rating || '-'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Comments</div>
                          <div className="text-sm text-slate-700">{comp.second_level_comment || '-'}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Overall Rating & Summary */}
          <div className="border-t border-slate-200 pt-6 space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Overall Self Rating (1-5)</Label>
              {isEditable ? (
                <RatingControl
                  value={selectedAppraisal.self_overall_rating}
                  onChange={(rating) => setSelectedAppraisal({ ...selectedAppraisal, self_overall_rating: rating })}
                  testId="self-overall-rating"
                />
              ) : (
                <div className="text-slate-900 font-semibold">{selectedAppraisal.self_overall_rating || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="self-summary" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Summary of Achievements</Label>
              {isEditable ? (
                <Textarea
                  id="self-summary"
                  value={selectedAppraisal.self_summary || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, self_summary: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="self-summary-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.self_summary || '-'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="self-goals" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Goals for Next Period</Label>
              {isEditable ? (
                <Textarea
                  id="self-goals"
                  value={selectedAppraisal.self_goals_next_period || ''}
                  onChange={(e) => setSelectedAppraisal({ ...selectedAppraisal, self_goals_next_period: e.target.value })}
                  className="border-slate-200 rounded-sm min-h-[100px]"
                  data-testid="self-goals-input"
                />
              ) : (
                <div className="text-sm text-slate-700">{selectedAppraisal.self_goals_next_period || '-'}</div>
              )}
            </div>
          </div>

          {/* Manager Summary (Read-only) */}
          {selectedAppraisal.manager_summary && (
            <div className="border-t border-slate-200 pt-6 space-y-4">
              <h3 className="text-xl font-medium text-slate-900">Manager Feedback</h3>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Overall Manager Rating</Label>
                <div className="text-slate-900 font-semibold">{selectedAppraisal.manager_overall_rating || '-'}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Manager Summary</Label>
                <div className="text-sm text-slate-700">{selectedAppraisal.manager_summary}</div>
              </div>
              {selectedAppraisal.manager_development_actions && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Development Actions</Label>
                  <div className="text-sm text-slate-700">{selectedAppraisal.manager_development_actions}</div>
                </div>
              )}
            </div>
          )}

          {/* 2nd Level Summary (Read-only) */}
          {selectedAppraisal.second_level_summary && (
            <div className="border-t border-slate-200 pt-6 space-y-4">
              <h3 className="text-xl font-medium text-slate-900">2nd-Level Calibration</h3>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Final Calibrated Rating</Label>
                <div className="text-slate-900 font-semibold">{selectedAppraisal.second_level_overall_rating || '-'}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Calibration Summary</Label>
                <div className="text-sm text-slate-700">{selectedAppraisal.second_level_summary}</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditable && (
            <div className="flex gap-4 border-t border-slate-200 pt-6">
              <Button
                onClick={handleSaveDraft}
                variant="outline"
                className="border-slate-200 rounded-sm"
                data-testid="save-draft-button"
              >
                Save Draft
              </Button>
              <Button
                onClick={handleSubmitSelf}
                className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
                data-testid="submit-self-button"
              >
                Submit Self Review
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="my-appraisals-title">
            {user.role === 'HR_ADMIN' ? 'All Appraisals' : 'My Appraisals'}
          </h1>
          <p className="text-sm text-slate-700 mt-2">
            {user.role === 'HR_ADMIN' 
              ? 'Manage all appraisals across the organization (Annual, Half-Yearly, or Quarterly Q1-Q4)'
              : 'Choose which appraisal cycle to work on (Annual, Half-Yearly, or Quarterly Q1-Q4)'
            }
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger as Child>
            <Button
              onClick={() => {
                setNewAppraisalData({
                  cycle_name: settings?.cycle_name || '',
                  cycle_year: settings?.cycle_year || '',
                  cycle_type: 'Annual'
                });
                setShowCreateDialog(true);
              }}
              className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
              data-testid="create-appraisal-button"
            >
              <Plus className="mr-2 h-4 w-4" />
              Start New Appraisal
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border border-slate-200 rounded-sm">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-slate-900">Create New Appraisal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                  Cycle Type
                </Label>
                <Select
                  value={newAppraisalData.cycle_type}
                  onValueChange={(value) => setNewAppraisalData({ ...newAppraisalData, cycle_type: value })}
                >
                  <SelectTrigger className="border-slate-200 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Annual">Annual Review</SelectItem>
                    <SelectItem value="Half-Yearly">Half-Yearly Review</SelectItem>
                    <SelectItem value="Q1">Q1 (Quarter 1)</SelectItem>
                    <SelectItem value="Q2">Q2 (Quarter 2)</SelectItem>
                    <SelectItem value="Q3">Q3 (Quarter 3)</SelectItem>
                    <SelectItem value="Q4">Q4 (Quarter 4)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                  Cycle Name
                </Label>
                <Input
                  value={newAppraisalData.cycle_name}
                  onChange={(e) => setNewAppraisalData({ ...newAppraisalData, cycle_name: e.target.value })}
                  placeholder="e.g., FY2025-2026"
                  className="border-slate-200 rounded-sm"
                />
              </div>
              
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                  Year
                </Label>
                <Input
                  value={newAppraisalData.cycle_year}
                  onChange={(e) => setNewAppraisalData({ ...newAppraisalData, cycle_year: e.target.value })}
                  placeholder="e.g., 2026"
                  className="border-slate-200 rounded-sm"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleCreateAppraisal}
                  className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Create Appraisal
                </Button>
                <Button
                  onClick={() => setShowCreateDialog(false)}
                  variant="outline"
                  className="border-slate-200 rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-slate-200 bg-white rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="my-appraisals-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Type</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cycle</th>
                {user.role === 'HR_ADMIN' && (
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Employee</th>
                )}
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Self Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">2nd-Level Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Updated</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {appraisals.length === 0 ? (
                <tr>
                  <td colSpan={user.role === 'HR_ADMIN' ? '10' : '9'} className="text-center p-8 text-slate-500">
                    No appraisals found. {user.role === 'HR_ADMIN' ? 'No appraisals in the system yet.' : 'Click "Start New Appraisal" to create one.'}
                  </td>
                </tr>
              ) : (
                appraisals.map((appraisal) => (
                  <tr key={appraisal.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`appraisal-row-${appraisal.id}`}>
                    <td className="p-4">{getCycleTypeBadge(appraisal.cycle_type)}</td>
                    <td className="p-4 text-slate-700">{appraisal.cycle_name} {appraisal.cycle_year}</td>
                    {user.role === 'HR_ADMIN' && (
                      <td className="p-4 text-slate-700 font-medium">{appraisal.employee_name}</td>
                    )}
                    <td className="p-4">{getStatusBadge(appraisal.status)}</td>
                    <td className="p-4 text-slate-700">{appraisal.manager_name}</td>
                    <td className="p-4">{getRatingDisplay(appraisal.self_overall_rating)}</td>
                    <td className="p-4">{getRatingDisplay(appraisal.manager_overall_rating)}</td>
                    <td className="p-4">{getRatingDisplay(appraisal.second_level_overall_rating)}</td>
                    <td className="p-4 text-slate-500">{formatDistanceToNow(new Date(appraisal.updated_at), { addSuffix: true })}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setSelectedAppraisal(appraisal)}
                          variant="outline"
                          size="sm"
                          className="border-slate-200 rounded-sm"
                          data-testid={`view-appraisal-${appraisal.id}`}
                        >
                          Select
                        </Button>
                        {user.role === 'HR_ADMIN' && (
                          <Button
                            onClick={() => handleDeleteAppraisal(appraisal.id, `${appraisal.cycle_name} ${appraisal.cycle_year}`)}
                            variant="outline"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                            data-testid={`delete-appraisal-${appraisal.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default MyAppraisals;
